import React, { useState, useEffect, useCallback } from 'react';
import { X, Upload, FileText, ChevronRight, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { adminFileService } from '@/services/adminFileService';
import { adminProductsService } from '@/services/adminProducts.service';
import { courseCurriculumService } from '@/services/courseCurriculum.service';
import type { FileLevelType, FileUploadProgress } from '@/types/courseFile.types';
import type { CourseCurriculum } from '@/types/courseCurriculum.types';

interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
}

interface CourseOption {
  id: string;
  title: string;
  slug: string;
}

export default function FileUploadModal({
  isOpen,
  onClose,
  onUploadSuccess,
}: FileUploadModalProps) {
  // Course selection state
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [curriculum, setCurriculum] = useState<CourseCurriculum | null>(null);

  // Level selection state
  const [levelType, setLevelType] = useState<FileLevelType>('topic');
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedLesson, setSelectedLesson] = useState('');
  const [selectedSegment, setSelectedSegment] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');

  // File state
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<FileUploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Fetch courses on mount
  useEffect(() => {
    if (isOpen) {
      fetchCourses();
      resetForm();
    }
  }, [isOpen]);

  // Fetch curriculum when course selected
  useEffect(() => {
    if (selectedCourse) {
      fetchCurriculum(selectedCourse);
      setSelectedModule('');
      setSelectedLesson('');
      setSelectedSegment('');
      setSelectedTopic('');
    }
  }, [selectedCourse]);

  // Reset lower selections when parent changes
  useEffect(() => {
    setSelectedLesson('');
    setSelectedSegment('');
    setSelectedTopic('');
  }, [selectedModule]);

  useEffect(() => {
    setSelectedSegment('');
    setSelectedTopic('');
  }, [selectedLesson]);

  useEffect(() => {
    setSelectedTopic('');
  }, [selectedSegment]);

  const fetchCourses = async () => {
    try {
      const response = await adminProductsService.fetchCourses();
      if (response.success && response.data) {
        setCourses(response.data.map((c: any) => ({
          id: c._id || c.id,
          title: c.title,
          slug: c.slug,
        })));
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };

  const fetchCurriculum = async (courseId: string) => {
    try {
      const response = await courseCurriculumService.getCourseCurriculumByCourseId(courseId);
      if (response.success && response.data) {
        setCurriculum(response.data);
      }
    } catch (err) {
      console.error('Error fetching curriculum:', err);
    }
  };

  const resetForm = () => {
    setSelectedCourse('');
    setCurriculum(null);
    setLevelType('topic');
    setSelectedModule('');
    setSelectedLesson('');
    setSelectedSegment('');
    setSelectedTopic('');
    setFile(null);
    setTitle('');
    setDescription('');
    setError(null);
    setSuccess(false);
    setUploadProgress(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const validation = adminFileService.validateFile(selectedFile);
      if (!validation.valid) {
        setError(validation.error || 'Invalid file');
        return;
      }
      setFile(selectedFile);
      setError(null);
      // Auto-fill title from filename
      if (!title) {
        const name = selectedFile.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
        setTitle(name.charAt(0).toUpperCase() + name.slice(1));
      }
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const validation = adminFileService.validateFile(droppedFile);
      if (!validation.valid) {
        setError(validation.error || 'Invalid file');
        return;
      }
      setFile(droppedFile);
      setError(null);
      if (!title) {
        const name = droppedFile.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
        setTitle(name.charAt(0).toUpperCase() + name.slice(1));
      }
    }
  }, [title]);

  const getLevelId = (): string => {
    switch (levelType) {
      case 'module': return selectedModule;
      case 'lesson': return selectedLesson;
      case 'segment': return selectedSegment;
      case 'topic': return selectedTopic;
      default: return '';
    }
  };

  const isFormValid = (): boolean => {
    if (!selectedCourse || !file || !title) return false;
    const levelId = getLevelId();
    return levelId !== '';
  };

  const handleUpload = async () => {
    if (!isFormValid()) return;

    setIsLoading(true);
    setError(null);

    try {
      await adminFileService.uploadFile(
        {
          courseId: selectedCourse,
          levelType,
          levelId: getLevelId(),
          title,
          description,
          file: file!,
        },
        setUploadProgress
      );

      setSuccess(true);
      setTimeout(() => {
        onUploadSuccess();
        handleClose();
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const modules = curriculum?.course?.modules || [];
  const selectedModuleData = modules.find(m => m.id === selectedModule);
  const lessons = selectedModuleData?.lessons || [];
  const selectedLessonData = lessons.find(l => l.id === selectedLesson);
  const segments = selectedLessonData?.segments || [];
  const selectedSegmentData = segments.find(s => s.id === selectedSegment);
  const topics = selectedSegmentData?.topics || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-slate-900 border border-white/10 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-600/20 rounded-lg">
              <FileText className="w-5 h-5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Upload Course File</h2>
              <p className="text-sm text-gray-400">Add resources to any curriculum level</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Course Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Course</label>
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-indigo-500"
            >
              <option value="">Select a course...</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>{course.title}</option>
              ))}
            </select>
          </div>

          {/* Level Type Selection */}
          {selectedCourse && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Attach to Level</label>
              <div className="grid grid-cols-4 gap-2">
                {(['module', 'lesson', 'segment', 'topic'] as FileLevelType[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => setLevelType(type)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      levelType === type
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-800 text-gray-400 hover:bg-slate-700'
                    }`}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Files attached at higher levels are available to all children below.
              </p>
            </div>
          )}

          {/* Hierarchy Selection */}
          {selectedCourse && curriculum && (
            <div className="space-y-4">
              {/* Module */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Module</label>
                <select
                  value={selectedModule}
                  onChange={(e) => setSelectedModule(e.target.value)}
                  className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-indigo-500"
                >
                  <option value="">Select module...</option>
                  {modules.map((m) => (
                    <option key={m.id} value={m.id}>{m.id}. {m.title}</option>
                  ))}
                </select>
              </div>

              {/* Lesson - show if level type requires it */}
              {selectedModule && levelType !== 'module' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Lesson</label>
                  <select
                    value={selectedLesson}
                    onChange={(e) => setSelectedLesson(e.target.value)}
                    className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-indigo-500"
                  >
                    <option value="">Select lesson...</option>
                    {lessons.map((l) => (
                      <option key={l.id} value={l.id}>{l.id}. {l.title}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Segment */}
              {selectedLesson && (levelType === 'segment' || levelType === 'topic') && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Segment</label>
                  <select
                    value={selectedSegment}
                    onChange={(e) => setSelectedSegment(e.target.value)}
                    className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-indigo-500"
                  >
                    <option value="">Select segment...</option>
                    {segments.map((s) => (
                      <option key={s.id} value={s.id}>{s.id}. {s.title}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Topic */}
              {selectedSegment && levelType === 'topic' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Topic</label>
                  <select
                    value={selectedTopic}
                    onChange={(e) => setSelectedTopic(e.target.value)}
                    className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-indigo-500"
                  >
                    <option value="">Select topic...</option>
                    {topics.map((t) => (
                      <option key={t.id} value={t.id}>{t.id}. {t.content?.substring(0, 50)}...</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Breadcrumb Preview */}
          {getLevelId() && (
            <div className="flex items-center gap-2 text-sm text-gray-400 bg-slate-800/50 rounded-lg p-3">
              <span className="text-indigo-400">Path:</span>
              <span>{levelType}</span>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">{getLevelId()}</span>
            </div>
          )}

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">File</label>
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                file ? 'border-green-500/50 bg-green-500/10' : 'border-white/20 hover:border-indigo-500/50'
              }`}
            >
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-green-400" />
                  <div className="text-left">
                    <p className="text-white font-medium">{file.name}</p>
                    <p className="text-sm text-gray-400">{adminFileService.formatFileSize(file.size)}</p>
                  </div>
                  <button
                    onClick={() => setFile(null)}
                    className="p-1 hover:bg-white/10 rounded"
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400 mb-2">Drag and drop your file here, or</p>
                  <label className="cursor-pointer">
                    <span className="text-indigo-400 hover:text-indigo-300">browse to upload</span>
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleFileSelect}
                      accept={ALLOWED_EXTENSIONS.join(',')}
                    />
                  </label>
                  <p className="text-xs text-gray-500 mt-2">Max 1GB. PDF, DOC, XLS, Images, etc.</p>
                </>
              )}
            </div>
          </div>

          {/* Title & Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Display title for the file"
              className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the file contents"
              rows={2}
              className="w-full bg-slate-800 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-indigo-500 resize-none"
            />
          </div>

          {/* Upload Progress */}
          {uploadProgress && (
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                {uploadProgress.status === 'complete' ? (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                ) : uploadProgress.status === 'error' ? (
                  <AlertCircle className="w-5 h-5 text-red-400" />
                ) : (
                  <Loader2 className="w-5 h-5 text-indigo-400 animate-spin" />
                )}
                <span className="text-sm text-gray-300">{uploadProgress.message}</span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${
                    uploadProgress.status === 'error' ? 'bg-red-500' :
                    uploadProgress.status === 'complete' ? 'bg-green-500' : 'bg-indigo-500'
                  }`}
                  style={{ width: `${uploadProgress.progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Error/Success Messages */}
          {error && (
            <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              <p className="text-green-400 text-sm">File uploaded successfully!</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="px-6 py-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!isFormValid() || isLoading}
            className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload File
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

const ALLOWED_EXTENSIONS = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv',
  '.ppt', '.pptx', '.png', '.jpg', '.jpeg', '.gif', '.webp',
  '.zip', '.rar', '.py', '.js', '.ts', '.json', '.html', '.css',
  '.txt', '.md'
];
